import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ProjectSetupPage from './pages/ProjectSetupPage';
import DashboardPage from './pages/DashboardPage';
import QuestionnairesPage from './pages/QuestionnairesPage';
import QuestionnaireDetailPage from './pages/QuestionnaireDetailPage';
import './App.css';

// Loading component
const LoadingScreen: React.FC = () => (
  <div className="loading-screen">
    <div className="loading-spinner"></div>
    <p>Loading...</p>
  </div>
);

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, session, needsProjectSetup, userContextLoaded } = useAuth();

  // Show loading if auth is loading OR if we have a session but user context hasn't loaded yet
  if (isLoading || (session && !userContextLoaded)) {
    return <LoadingScreen />;
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (needsProjectSetup) {
    return <Navigate to="/setup-project" replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Setup Route - requires session but not full auth
const SetupRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoading, session, needsProjectSetup, isAuthenticated, userContextLoaded } = useAuth();

  // Show loading if auth is loading OR if we have a session but user context hasn't loaded yet
  if (isLoading || (session && !userContextLoaded)) {
    return <LoadingScreen />;
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (!needsProjectSetup && isAuthenticated) {
    return <Navigate to="/questionnaires" replace />;
  }

  return <>{children}</>;
};

// Determine redirect path for authenticated users
function getAuthenticatedRedirect(needsProjectSetup: boolean, isAuthenticated: boolean): string {
  if (needsProjectSetup) {
    return '/setup-project';
  }
  if (isAuthenticated) {
    return '/questionnaires';
  }
  // User has session but no project and doesn't need setup (project exists but they're not a member)
  // This means they need an invite - show them a message or redirect to setup
  return '/setup-project';
}

function AppRoutes() {
  const { isAuthenticated, isLoading, session, needsProjectSetup, userContextLoaded } = useAuth();

  // Show loading if auth is loading OR if we have a session but user context hasn't loaded yet
  if (isLoading || (session && !userContextLoaded)) {
    return <LoadingScreen />;
  }

  const authRedirect = session ? getAuthenticatedRedirect(needsProjectSetup, isAuthenticated) : '/login';

  return (
    <Routes>
      <Route
        path="/login"
        element={session ? <Navigate to={authRedirect} replace /> : <LoginPage />}
      />
      <Route
        path="/signup"
        element={session ? <Navigate to={authRedirect} replace /> : <SignupPage />}
      />
      <Route
        path="/setup-project"
        element={
          <SetupRoute>
            <ProjectSetupPage />
          </SetupRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/questionnaires"
        element={
          <ProtectedRoute>
            <QuestionnairesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/questionnaires/:id"
        element={
          <ProtectedRoute>
            <QuestionnaireDetailPage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to={isAuthenticated ? "/questionnaires" : "/login"} replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
